import type { FastifyReply, FastifyRequest } from 'fastify'
import type { LiveSocialResponse } from '@my-roadtrip/types'
import { ApiResponse } from '../../utils/response.js'
import { createLogger } from '../../common/utils/logger.js'
import { LiveService, LiveServiceError } from './live.service.js'

type ClanParams = { clanId: string }

export class LiveController {
  private readonly logger = createLogger('LiveController')

  constructor(private readonly service: LiveService) {}

  async getFriendLocations(
    request: FastifyRequest,
    reply: FastifyReply
  ) {
    const userId = request.user?.uid
    if (!userId) {
      this.logger.warn('Unauthorized getFriendLocations request', {
        requestId: String(request.id ?? ''),
        ip: request.ip
      })
      return ApiResponse.unauthorized(reply)
    }

    try {
      this.logger.debug('Fetching friend locations', { userId })
      const payload: LiveSocialResponse = await this.service.getFriendLocations(userId)
      this.logger.info('Friend locations returned', {
        userId,
        count: payload.users.length
      })
      return reply.send(payload)
    } catch (error) {
      return this.handleError(error, reply, 'Failed to load friend locations')
    }
  }

  async getClanLocations(
    request: FastifyRequest<{ Params: ClanParams }>,
    reply: FastifyReply
  ) {
    const userId = request.user?.uid
    if (!userId) {
      this.logger.warn('Unauthorized getClanLocations request', {
        requestId: String(request.id ?? ''),
        ip: request.ip,
        clanId: request.params.clanId
      })
      return ApiResponse.unauthorized(reply)
    }

    try {
      this.logger.debug('Fetching clan locations', {
        userId,
        clanId: request.params.clanId
      })
      const payload: LiveSocialResponse = await this.service.getClanLocations(userId, request.params.clanId)
      this.logger.info('Clan live view returned', {
        userId,
        clanId: request.params.clanId,
        count: payload.users.length
      })
      return reply.send(payload)
    } catch (error) {
      return this.handleError(error, reply, 'Failed to load clan live view')
    }
  }

  private handleError(error: unknown, reply: FastifyReply, fallbackMessage: string) {
    if (error instanceof LiveServiceError) {
      this.logger.warn('LiveServiceError', {
        message: error.message,
        statusCode: error.statusCode
      })
      switch (error.statusCode) {
        case 403:
          return ApiResponse.forbidden(reply, error.message)
        case 404:
          return ApiResponse.notFound(reply, error.message)
        default:
          return ApiResponse.error(reply, error.statusCode, error.message)
      }
    }

    this.logger.error('Unexpected live controller error', {
      error: error instanceof Error ? error.message : String(error)
    })
    return ApiResponse.internalError(reply, fallbackMessage)
  }
}
